import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';

export const useVerifyReports = () => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setReports([]);
      setIsLoading(false);
      return;
    }

    const fetchReports = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('verify_reports')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;

        setReports(data || []);
      } catch (err) {
        console.error('Error fetching verify reports:', err);
        setError(err.message || 'Failed to fetch reports');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();

    // Set up real-time subscription
    const subscription = supabase
      .channel('verify_reports_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'verify_reports',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          fetchReports();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, isAuthenticated]);

  const createReport = async (reportData) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const { data, error: createError } = await supabase
        .from('verify_reports')
        .insert([{
          ...reportData,
          user_id: user.id
        }])
        .select()
        .single();

      if (createError) throw createError;
      return data;
    } catch (err) {
      console.error('Error creating report:', err);
      throw err;
    }
  };

  const updateReport = async (reportId, updates) => {
    try {
      const { error: updateError } = await supabase
        .from('verify_reports')
        .update(updates)
        .eq('id', reportId)
        .eq('user_id', user?.id);

      if (updateError) throw updateError;
    } catch (err) {
      console.error('Error updating report:', err);
      throw err;
    }
  };

  const deleteReport = async (reportId) => {
    try {
      const { error: deleteError } = await supabase
        .from('verify_reports')
        .delete()
        .eq('id', reportId)
        .eq('user_id', user?.id);

      if (deleteError) throw deleteError;
    } catch (err) {
      console.error('Error deleting report:', err);
      throw err;
    }
  };

  const logVisit = async (reportId, visitorInfo = {}) => {
    try {
      const { error: visitError } = await supabase
        .from('verify_report_visits')
        .insert([{
          report_id: reportId,
          visited_at: new Date().toISOString(),
          ip_address: visitorInfo.ip_address,
          user_agent: visitorInfo.user_agent,
          referrer: visitorInfo.referrer,
        }]);

      if (visitError) throw visitError;

      // Update visit count on report
      const { data: report } = await supabase
        .from('verify_reports')
        .select('visit_count')
        .eq('id', reportId)
        .single();

      await supabase
        .from('verify_reports')
        .update({ visit_count: (report?.visit_count || 0) + 1 })
        .eq('id', reportId);
    } catch (err) {
      console.error('Error logging visit:', err);
      // Don't throw - visit logging shouldn't break the app
    }
  };

  return {
    reports,
    isLoading,
    error,
    createReport,
    updateReport,
    deleteReport,
    logVisit,
    refetch: async () => {
      // Trigger refetch by updating state
      setIsLoading(true);
    }
  };
};
